import React, { useEffect, useState } from "react";
import AOS from "aos";
import axios from "axios";
import { base_url } from "../../Constant";
import "aos/dist/aos.css"; // Ensure AOS is imported

const TeamMember = ({ member, delay }) => {
  // Handle base64 encoded images
  const getImageSrc = (image) => {
    if (!image) return ""; // Fallback if no image
    
    // Check if it's already a data URI
    if (image.startsWith("data:")) {
      return image;
    }
    
    // Check if it looks like base64 (no slashes for URLs)
    if (!/^https?:\/\//.test(image)) {
      // Assume it's base64 and add proper prefix
      // Detect image type from content or default to jpeg
      return `data:image/jpeg;base64,${image}`;
    }
    
    // Return as-is if it's a URL
    return image;
  };

  return (
    <div
      className="col-lg-3 col-md-6 d-flex align-items-stretch"
      data-aos="fade-up"
      data-aos-delay={delay}
    >
      <div className="team-member">
        <div className="member-img">
          <img 
            src={getImageSrc(member.image)} 
            className="img-fluid" 
            alt={member.name}
            onError={(e) => {
              // Fallback for broken images
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%23f0f0f0' width='200' height='200'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%23999'%3EImage Not Available%3C/text%3E%3C/svg%3E";
            }}
          />
          {member.socialLinks && (
            <div className="social">
              <a href={member.socialLinks.twitter}>
                <i className="bi bi-twitter-x"></i>
              </a>
              <a href={member.socialLinks.facebook}>
                <i className="bi bi-facebook"></i>
              </a>
              <a href={member.socialLinks.instagram}>
                <i className="bi bi-instagram"></i>
              </a>
              <a href={member.socialLinks.linkedin}>
                <i className="bi bi-linkedin"></i>
              </a>
            </div>
          )}
        </div>
        <div className="member-info">
          <h4>{member.name}</h4>
          {member?.position && <span>{member.position}</span>}
        </div>
      </div>
    </div>
  );
};

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    AOS.init();
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${base_url}/members`, {
        timeout: 10000, // 10 second timeout
      });
      
      // Validate response structure
      if (!response.data) {
        throw new Error("Empty response from server");
      }

      if (response.data.success !== true) {
        throw new Error(
          response.data.message || "API returned unsuccessful status"
        );
      }

      if (!Array.isArray(response.data.data)) {
        throw new Error("Invalid data format: expected an array");
      }

      // Validate and map members data
      const membersData = response.data.data
        .filter((member) => member && member.name) // Filter out invalid entries
        .map((member) => {
          if (!member.name || typeof member.name !== "string") {
            console.warn("Skipping invalid member:", member);
            return null;
          }
          return {
            name: member.name.trim(),
            position: member.position || "Member",
            image: member.photo || "", // API returns base64 photo
            socialLinks: member.socialLinks || null,
          };
        })
        .filter((member) => member !== null);

      if (membersData.length === 0) {
        throw new Error("No valid team members found in response");
      }

      setTeamMembers(membersData);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      let errorMessage = "Failed to load team members";

      if (err.response) {
        // Server responded with error status
        errorMessage = `Server Error (${err.response.status}): ${
          err.response.data?.message || err.response.statusText
        }`;
      } else if (err.request) {
        // Request made but no response
        errorMessage = "No response from server. Please check your connection.";
      } else if (err.message) {
        // Error in request setup or validation
        errorMessage = err.message;
      }

      console.error("Error fetching team members:", {
        error: err,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      });

      setError(errorMessage);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    fetchTeamMembers();
  };

  // Loading state
  if (loading) {
    return (
      <section id="team" className="team section">
        <div className="container">
          <div className="text-center">
            <p>Loading team members...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state with retry button
  if (error) {
    return (
      <section id="team" className="team section">
        <div className="container">
          <div className="text-center" style={{ padding: "40px 20px" }}>
            <div
              style={{
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                borderRadius: "4px",
                padding: "15px",
                color: "#721c24",
              }}
            >
              <h4>Unable to Load Team Members</h4>
              <p>{error}</p>
              <button
                onClick={handleRetry}
                style={{
                  marginTop: "10px",
                  padding: "8px 16px",
                  backgroundColor: "#721c24",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Try Again {retryCount > 0 && `(${retryCount})`}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (teamMembers.length === 0) {
    return (
      <section id="team" className="team section">
        <div className="container">
          <div className="text-center">
            <p>No team members available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  // Success state
  return (
    <section id="team" className="team section">
      <div className="container">
        <div className="row gy-4">
          {teamMembers.map((member, index) => (
            <TeamMember key={index} member={member} delay={(index + 1) * 100} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
